import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import locale from 'antd/es/date-picker/locale/ko_KR';
import { Calendar, Flex } from 'antd';
import type { CalendarProps } from 'antd';
import type { Dayjs } from 'dayjs';
import dayLocaleData from 'dayjs/plugin/localeData';
import { createStyles } from 'antd-style';
import classNames from 'classnames';
import { ReactComponent as ArrowLeft } from "../../../../../../assets/images/icon/arrow-left.svg";
import { ReactComponent as ArrowRight } from "../../../../../../assets/images/icon/arrow-right.svg";
import './index.css';

dayjs.extend(dayLocaleData);

interface IProps {
  onChange?: (date: string) => void
  onActiveDateList: Array<string>
  initDate?: string;
  fullBookingDateList?: Dayjs[]
}

const useStyle = createStyles(({ token, css, cx }) => {
  const weekend = css`
      color: var(--secondarys800);
      &.gray {
        opacity: 0.4;
      }
    `;
  return {
    wrapper: css`
        width: 100%;
      `,
    dateCell: css`
        position: relative;
        &:before {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          margin: auto;
          width: 36px;
          height: 36px;
          border-radius: 100%;
          border: 1px solid transparent;
          box-sizing: border-box;
        }
      `,
    today: css`
        &:before {
          background: var(--secondarys100);
        }
      `,
    disabled: css`
    `,
    text: css`
      `,
    current: css`
        color: var(--secondarys800);
        &:before {
          background: var(--primaryp500);
        }
        &:hover:before {
          background: var(--primaryp500);
          opacity: 0.8;
        }
        .${cx(weekend)} {
          color: #000000;
        }
      `,
    monthCell: css`
        width: 120px;
        color: ${token.colorTextBase};
        border-radius: ${token.borderRadiusOuter}px;
        padding: 5px 0;
        &:hover {
          background: rgba(0, 0, 0, 0.04);
        }
      `,
    monthCellCurrent: css`
        color: ${token.colorTextLightSolid};
        background: ${token.colorPrimary};
        &:hover {
          background: ${token.colorPrimary};
          opacity: 0.8;
        }
      `,
    weekend,
  };
});

const WellnessProgramBookCalendar = ({ initDate, onChange, onActiveDateList, fullBookingDateList = [] }: IProps) => {
  const [date, setDate] = useState<string | undefined>(initDate ?? dayjs().format('YYYY-MM-DD'))
  const [selectedDate, setSelectedDate] = useState<string | undefined>(initDate);
  const { styles } = useStyle();
  const [panelDateDate, setPanelDate] = React.useState<Dayjs>(dayjs(date, 'YYYY-MM-DD'));
  const onPanelChange = (value: Dayjs, mode: CalendarProps<Dayjs>['mode']) => {
    setPanelDate(value);
    setDate(value.format('YYYY-MM-DD'))
  };

  const cellRender: CalendarProps<Dayjs>['cellRender'] = (date, info) => {
    if (!panelDateDate.isSame(date, 'month')) return <></>;
    const isWeekend = date.day() === 6 || date.day() === 0;
    const isPastDate = dayjs().isAfter(date, 'day');
    const isActiveDate = onActiveDateList.some(onActiveDateList => dayjs(onActiveDateList).isSame(date, 'date'));
    const isFullBookingDate = fullBookingDateList?.some(fullBookingDate => dayjs(fullBookingDate).isSame(date, 'date'));
    if (info.type === 'date') {
      return React.cloneElement(info.originNode, {
        ...info.originNode.props,
        className: classNames(styles.dateCell, {
          [styles.current]: selectedDate && dayjs(selectedDate).isSame(date, 'date'),
          [styles.today]: date.isSame(dayjs(), 'date'),
          [styles.disabled]: isPastDate,
        }),
        children: (
          <div className={classNames(styles.text) + ' body/b3'} style={(isPastDate || !isActiveDate) || isFullBookingDate ? { position: 'relative', zIndex: 1, color: 'var(--secondarys300)', cursor: 'auto', pointerEvents: 'none' } : { position: 'relative', zIndex: 1 }}>
            <span
              className={classNames({
                [styles.weekend]: isWeekend,
                gray: !panelDateDate.isSame(date, 'month'),
              })}
              style={(isPastDate || !isActiveDate) || isFullBookingDate ? { position: 'relative', zIndex: 1, color: 'var(--secondarys300)', cursor: 'auto', pointerEvents: 'none' } : { position: 'relative', zIndex: 1, }}
            >
              {date.get('date')}
            </span>
            {isFullBookingDate && <div style={{ position: 'absolute', fontSize: 10, fontWeight: 400, lineHeight: 'normal', fontStyle: 'normal', display: 'flex', justifyContent: 'center', width: '100%' }}>마감</div>}
          </div >
        ),
      });
    }

    return info.originNode;
  };

  const onDateChange: CalendarProps<Dayjs>['onSelect'] = (value, selectInfo) => {
    if (selectInfo.source === 'date') {
      const isActiveDate = onActiveDateList.some(onActiveDateList => dayjs(onActiveDateList).isSame(dayjs(value), 'date'));
      const isFullBookingDate = fullBookingDateList?.some(fullBookingDate => dayjs(fullBookingDate).isSame(dayjs(value), 'date'));
      if (isActiveDate && !isFullBookingDate) {
        setDate(value.format('YYYY-MM-DD'))
        setSelectedDate(value.format('YYYY-MM-DD'));
      }
    }
  };

  useEffect(() => {
    selectedDate && onChange?.(selectedDate)
  }, [selectedDate])

  return <>
    <div className={styles.wrapper + ' custom-calendar-wellness-program'}>
      <Calendar
        disabledDate={date => !date.isSame(dayjs(panelDateDate), 'month')}
        locale={{ ...locale, lang: { ...locale.lang, shortWeekDays: ["일", "월", "화", "수", "목", "금", "토"] } }}
        fullscreen={false}
        defaultValue={initDate ? dayjs(initDate, 'YYYY-MM-DD') : undefined}
        headerRender={({ value, onChange }) => {
          return <>
            <div>
              <Flex style={{ justifyContent: 'center', alignItems: 'start' }}>
                <ArrowLeft width={16} height={16} stroke={value.endOf("month") >= dayjs().add(-1, 'M').endOf("month") ? "var(--secondarys400)" : '#cdcdcd'}
                  style={{ cursor: "pointer", padding: '1.5px 0', pointerEvents: value.endOf("month") >= dayjs().add(-1, 'M').endOf("month") ? 'auto' : 'none' }}
                  onClick={() => value.endOf("month") >= dayjs().add(-2, 'M').endOf("month") && onChange(value.clone().add(-1, 'month'))} />

                <div className="sub-title/st3" style={{ paddingInline: 12, paddingBottom: 8 }}>{`${value.year()}. ${value.month() + 1}`}</div>

                <ArrowRight width={16} height={16} stroke={value.endOf("month") <= dayjs().add(1, 'month').endOf("month") ? "var(--secondarys400)" : '#cdcdcd'}
                  style={{ cursor: "pointer", padding: '1.5px 0', pointerEvents: value.endOf("month") <= dayjs().add(1, 'month').endOf("month") ? 'auto' : 'none' }}
                  onClick={() => value.endOf("month") <= dayjs().add(1, 'month').endOf("month") && onChange(value.clone().add(+1, 'month'))} />
              </Flex>
            </div>
          </>
        }}
        fullCellRender={cellRender}
        onPanelChange={onPanelChange}
        onSelect={onDateChange}
      />
    </div>
  </>
}

export default WellnessProgramBookCalendar